import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { NgxMaskDirective } from "ngx-mask";

export interface CreditCardData {
  cardNumber: string;
  cardHolder: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  brand?: string;
}

@Component({
  selector: "app-credit-card",
  imports: [ReactiveFormsModule, NgxMaskDirective],
  templateUrl: "./credit-card.component.html",
  styleUrls: ["./credit-card.component.scss"],
})
export class CreditCardComponent implements OnInit, OnChanges {
  @Input() cardData: CreditCardData = {
    cardNumber: "",
    cardHolder: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    brand: "Visa",
  };

  @Input() isFlipped = false;
  @Input() showCvvField = false;
  @Input() readonly = false;

  @Output() cardDataChange = new EventEmitter<CreditCardData>();
  @Output() flipCard = new EventEmitter<void>();
  @Output() focusCvv = new EventEmitter<void>();

  form!: FormGroup;

  formattedCardNumber = "";
  formattedExpiry = "";
  cardBrand = "Credit Card";

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.form = this.fb.group({
      // Número: 13 a 16 dígitos (a máscara já limita o formato).
      cardNumber: [
        this.cardData.cardNumber,
        [Validators.required, this.cardNumberValidator],
      ],
      cardHolder: [
        this.cardData.cardHolder,
        [Validators.required, Validators.maxLength(26)],
      ],
      // Mês e Ano: exatamente 2 dígitos (MM / YY).
      expiryMonth: [
        this.cardData.expiryMonth,
        [Validators.required, Validators.pattern(/^\d{2}$/)],
      ],
      expiryYear: [
        this.cardData.expiryYear,
        [Validators.required, Validators.pattern(/^\d{2}$/)],
      ],
      // CVV: 3 ou 4 dígitos (Amex usa 4).
      cvv: [
        this.cardData.cvv,
        [Validators.required, Validators.pattern(/^\d{3,4}$/)],
      ],
    });

    // sincroniza visual e emite para o pai
    this.form.valueChanges.subscribe((v) => {
      const next: CreditCardData = {
        ...v,
        brand: this.detectCardBrand(v.cardNumber),
      };
      this.cardBrand = next.brand ?? "Credit Card";
      this.updateFormatted(next);
      this.cardDataChange.emit(next);
    });

    // inicializa exibição formatada
    this.cardBrand = this.detectCardBrand(this.cardData.cardNumber);
    this.updateFormatted(this.cardData);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["cardData"] && this.form) {
      // atualiza o form sem reemitir (evita loop)
      this.form.patchValue(this.cardData, { emitEvent: false });
      this.cardBrand = this.detectCardBrand(this.cardData.cardNumber);
      this.updateFormatted(this.cardData);
    }
  }

  // Validade do form, consumida pelos containers para bloquear o envio.
  get isValid(): boolean {
    return this.form?.valid ?? false;
  }

  // Força a exibição dos erros (usado quando o container tenta enviar sem que
  // o usuário tenha saído dos campos).
  markAllAsTouched(): void {
    this.form?.markAllAsTouched();
  }

  // Campo inválido e já tocado (erro exibido só após o blur).
  isFieldInvalid(name: string): boolean {
    const control = this.form?.get(name);
    return !!control && control.touched && control.invalid;
  }

  // Valida o número do cartão: 13 a 16 dígitos (ignora espaços da máscara).
  // Vazio é responsabilidade do required.
  private cardNumberValidator = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const digits = String(control.value ?? "").replace(/\D/g, "");
    if (!digits) return null;
    return /^\d{13,16}$/.test(digits) ? null : { invalidCard: true };
  };

  // ===== helpers =====
  private updateFormatted(data: CreditCardData) {
    const cleaned = (data.cardNumber ?? "")
      .replace(/\s/g, "")
      .replace(/[^0-9]/g, "");
    const groups = cleaned.match(/.{1,4}/g) ?? [];
    this.formattedCardNumber = groups.length
      ? groups.join(" ")
      : "**** **** **** ****";

    const mm = (data.expiryMonth ?? "").toString().padStart(2, "0").slice(0, 2);
    const yy = (data.expiryYear ?? "").toString().slice(-2);
    this.formattedExpiry = mm && yy ? `${mm}/${yy}` : "MM/YY";
  }

  private detectCardBrand(cardNumber: string): string {
    const n = (cardNumber || "").replace(/\s/g, "");
    if (/^4/.test(n)) return "Visa";
    if (/^5[1-5]/.test(n)) return "Mastercard";
    if (/^3[47]/.test(n)) return "American Express";
    if (/^6(?:011|5)/.test(n)) return "Discover";
    if (/^3(?:0[0-5]|[68])/.test(n)) return "Diners Club";
    if (/^35/.test(n)) return "JCB";
    return "Credit Card";
  }

  onCvvClick() {
    if (!this.readonly) {
      this.flipCard.emit();
      this.focusCvv.emit();
    }
  }

  onCvvFocus() {
    if (!this.isFlipped) this.flipCard.emit();
  }

  getCardBrandIcon(): string {
    return "💳";
  }

  getCardBrandClass(): string {
    return (this.cardBrand || "credit-card").toLowerCase().replace(/\s+/g, "-");
  }
}
